import {DocumentedProductViewModel, ProductViewModel} from "./api/models";
import {common, docs as docsMessages, products} from "./i18n";
import {hasRequirements, sortedPlatforms} from "./components/requirements";
import {fromMinorUnits} from "./components/format";
import {absoluteUrl, SITE_DESCRIPTION, SITE_NAME, SITE_ORIGIN} from "./seo";

// schema.org descriptions of the site's pages (https://schema.org), for usePageMeta's jsonLd.

const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;

export const productPath = (sku: string) => `/products/${encodeURIComponent(sku)}`;
export const docsPath = (sku: string) => `/docs/${encodeURIComponent(sku)}`;

export function organization(): object {
    return {
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: SITE_NAME,
        url: absoluteUrl('/'),
        logo: absoluteUrl('/logo-512.png')
    };
}

export function website(): object {
    return {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: absoluteUrl('/'),
        description: SITE_DESCRIPTION,
        publisher: { '@id': ORGANIZATION_ID }
    };
}

export function productList(products: ProductViewModel[]): object {
    return {
        '@type': 'ItemList',
        itemListElement: products.map((product, i) => ({
            '@type': 'ListItem', position: i + 1, name: product.name, url: absoluteUrl(productPath(product.sku))
        }))
    };
}

// Offers only once it's on sale: Early Access prices are only planned.
export function softwareApplication(product: ProductViewModel, description: string): object {
    const systems = [...new Set(product.currentVersion?.files.map(x => x.operatingSystem).filter(x => x !== 'any') ?? [])];
    const requirements = hasRequirements(product.requirements) ? product.requirements : undefined;
    // From the requirements when there are platforms there (with their minimum versions), else the downloads'.
    const operatingSystem = requirements?.platforms.length
        ? sortedPlatforms(requirements).map(x => x.minimumVersion || products.requirements.platforms[x.platform]).join(', ')
        : systems.map(x => common.operatingSystems[x]).join(', ');
    const text = (key: string, value?: string) => value?.trim() ? { [key]: value.trim() } : {};
    return {
        '@type': 'SoftwareApplication',
        name: product.name,
        description,
        url: absoluteUrl(productPath(product.sku)),
        applicationCategory: 'MultimediaApplication',
        ...(operatingSystem ? { operatingSystem } : {}),
        ...text('processorRequirements', requirements?.processor),
        ...text('memoryRequirements', requirements?.memory),
        ...text('storageRequirements', requirements?.disk),
        ...(product.currentVersion ? { softwareVersion: product.currentVersion.version } : {}),
        ...(product.images.length ? { image: product.images.map(x => x.url) } : {}),
        ...(!product.beta && product.prices.length ? {
            offers: product.prices.map(x => ({
                '@type': 'Offer', name: x.edition, price: fromMinorUnits(x.amount, x.currency), priceCurrency: x.currency
            }))
        } : {}),
        publisher: { '@id': ORGANIZATION_ID }
    };
}

export const GLOSSARY_PATH = '/docs/glossary';
export const glossaryTermPath = (id: string) => `${GLOSSARY_PATH}#term-${id}`;

// The glossary as a set of defined terms, each addressable by its anchor.
export function glossaryData(name: string, description: string, entries: { id: string, term: string, definition: string }[]): object[] {
    const url = absoluteUrl(GLOSSARY_PATH);
    return [
        {
            '@type': 'DefinedTermSet',
            '@id': url,
            name,
            description,
            url,
            hasDefinedTerm: entries.map(x => ({
                '@type': 'DefinedTerm',
                '@id': absoluteUrl(glossaryTermPath(x.id)),
                name: x.term,
                description: x.definition,
                inDefinedTermSet: url
            }))
        },
        {
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: docsMessages.title, item: absoluteUrl('/docs') },
                { '@type': 'ListItem', position: 2, name, item: url }
            ]
        },
        organization()
    ];
}

export function documentation(docs: DocumentedProductViewModel, description: string): object[] {
    const url = absoluteUrl(docsPath(docs.sku));
    return [
        {
            '@type': 'TechArticle',
            headline: `${docs.productName} documentation`,
            description,
            url,
            ...(docs.modifiedOn ? { dateModified: docs.modifiedOn } : {}),
            about: { '@type': 'SoftwareApplication', name: docs.productName, url: absoluteUrl(productPath(docs.sku)) },
            publisher: { '@id': ORGANIZATION_ID }
        },
        {
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: docsMessages.title, item: absoluteUrl('/docs') },
                { '@type': 'ListItem', position: 2, name: docs.productName, item: url }
            ]
        },
        organization()
    ];
}
